import {ArrowForward, MoreVert, Person, Repeat} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {Box, IconButton, Switch, Typography} from '@mui/material';
import {ChangeEvent, FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {chargersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {ScheduleTypeEnum} from 'src/enums';
import {DayOfWeek, DayOfWeekShortValue} from 'src/types/pricing';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {compileScheduleDate} from 'src/utils/consts';
import {formatTime, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {ChargerScheduleUpdateWrapper} from '../ChargerScheduleUpdateWrapper';
import {DeleteSchedule} from '../DeleteSchedule';
import {
    actionsWrapperSx,
    arrowSx,
    editButtonSx,
    scheduleInfoWrapperSx,
    switchSx,
    timeWrapperSx,
    typographySx,
    userRowSx,
    wrapperSx,
} from './ScheduleItem.sx';
import {ScheduleItemType} from './ScheduleItem.types';

type UpdateScheduleStatusMutationTrigger = ReturnType<
    typeof chargersApi.useUpdateScheduleStatusMutation
>[0];

const handleSwitch =
    (
        trigger: UpdateScheduleStatusMutationTrigger,
        dispatch: AppDispatch,
        businessId: number,
        chargerId: number,
        scheduleChargingId: number,
        userId: number,
        companyId?: number
    ) =>
        async (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            dispatch(preloaderChangeStatus(true));
            try {
                await trigger({
                    businessId,
                    chargerId,
                    active: checked,
                    scheduleChargingId,
                    userId,
                    companyId
                }).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ScheduleItem: FC<ScheduleItemType> = ({data, businessId, chargerId, chargerData}) => {
    const [trigger] = chargersApi.useUpdateScheduleStatusMutation();
    const dispatch = useAppDispatch();
    const canDeleteSchedule = useCan('scheduleCharging.delete');
    const canUpdateSchedule = useCan('scheduleCharging.update');
    const {authInfo} = useAppSelector((state) => state.common);
    const intl = useIntl();

    const userId = authInfo?.id;
    const companyId = authInfo?.company?.id || authInfo?.employee?.company?.id;

    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'editSchedule'});

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };
    const scheduleUserName = data.user
        ? `${data.user.firstName} ${data.user.lastName}`
        : null;

    return (
        <Box sx={wrapperSx}>
            <Box sx={scheduleInfoWrapperSx}>
                <Box sx={timeWrapperSx}>
                    <Typography
                        variant="body1"
                        sx={typographySx}
                    >
                        {compileScheduleDate(DayOfWeekShortValue[data.startDay as DayOfWeek], [
                            formatTime(data.startHour),
                            formatTime(data.startMinute),
                        ])}
                    </Typography>
                    {data.type === ScheduleTypeEnum.ONE_TIME ? (
                        <ArrowForward sx={arrowSx}/>
                    ) : (
                        <Repeat sx={arrowSx}/>
                    )}
                    <Typography
                        variant="body1"
                        sx={typographySx}
                    >
                        {compileScheduleDate(DayOfWeekShortValue[data.stopDay as DayOfWeek], [
                            formatTime(data.stopHour),
                            formatTime(data.stopMinute),
                        ])}
                    </Typography>
                </Box>
                {scheduleUserName && (
                    <Box sx={userRowSx}>
                        <Person sx={{fontSize: '0.875rem'}}/>
                        <Typography variant="caption">{scheduleUserName}</Typography>
                    </Box>
                )}
            </Box>
            {/* Three controls, two rights: switching a schedule on and editing it are updates,
                removing it is a deletion. They shared one gate, so anybody who could edit could also
                delete - and the server would refuse the second. */}
            <Box sx={actionsWrapperSx}>
                {canUpdateSchedule && (
                    <Switch
                        checked={data.active}
                        onChange={userId ? handleSwitch(trigger, dispatch, businessId, chargerId, data.id, userId, companyId) : undefined}
                        sx={switchSx}
                    />
                                )}
                <Box>
                    {canDeleteSchedule && (
                        <DeleteSchedule
                            businessId={businessId}
                            chargerId={chargerId}
                            scheduleChargingId={data.id}
                        />
                                        )}
                    {canUpdateSchedule && (
                        <IconButton
                            sx={editButtonSx}
                            onClick={handleOpenModal}
                        >
                            <MoreVert/>
                        </IconButton>
                                        )}
                </Box>
            </Box>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ChargerScheduleUpdateWrapper
                    handleClose={handleCloseModal}
                    initialValue={data}
                    businessId={businessId}
                    chargerId={chargerId}
                    chargerData={chargerData}
                />
            </AMPModal>
        </Box>
    );
};
